interface EventMapperEntry {
  eventName: string;
  handler: EventListenerOrEventListenerObject;
  delay: number;
  timerId: number;
  reference: any; // .NET reference
  elementId: string | Document;
  properties: string[];
  projection: ((eventEntry: any, event: Event) => void) | null;
}

export default class ThrottledEventManager {
  private mapper: { [key: string]: EventMapperEntry };

  constructor() {
    this.mapper = {};
  }

  /**
   * Subscribes to an event on a specific element with throttling.
   * @param eventName - The name of the event to subscribe to.
   * @param elementId - The ID of the element to attach the event to.
   * @param projection - A string representing a function to project the event data.
   * @param throttleInterval - The interval in milliseconds to throttle the event.
   * @param key - A unique key to identify the subscription.
   * @param properties - An array of properties to extract from the event.
   * @param dotnetReference - A reference to the .NET object to invoke methods on.
   */
  subscribe(
    eventName: string,
    elementId: string,
    projection: string | null,
    throttleInterval: number,
    key: string,
    properties: string[],
    dotnetReference: any
  ): void {
    const handlerRef = this.throttleEventHandler.bind(this, key);

    const elem = document.getElementById(elementId);
    if (elem) {
      elem.addEventListener(eventName, handlerRef, false);

      let projector: ((eventEntry: any, event: Event) => void) | null = null;
      if (projection) {
        const parts = projection.split('.');
        let functionPointer: any = window;
        let functionReferenceFound = true;

        for (let i = 0; i < parts.length; i++) {
          functionPointer = functionPointer[parts[i]];
          if (!functionPointer) {
            functionReferenceFound = false;
            break;
          }
        }

        if (functionReferenceFound) {
          projector = functionPointer;
        }
      }

      this.mapper[key] = {
        eventName,
        handler: handlerRef,
        delay: throttleInterval,
        timerId: -1,
        reference: dotnetReference,
        elementId,
        properties,
        projection: projector,
      };
    }
  }

  /**
   * Subscribes to a global event with optional throttling.
   * @param eventName - The name of the event to subscribe to.
   * @param throttleInterval - The interval in milliseconds to throttle the event.
   * @param key - A unique key to identify the subscription.
   * @param properties - An array of properties to extract from the event.
   * @param dotnetReference - A reference to the .NET object to invoke methods on.
   */
  subscribeGlobal(
    eventName: string,
    throttleInterval: number,
    key: string,
    properties: string[],
    dotnetReference: any
  ): void {
    const handlerRef = throttleInterval > 0
      ? this.throttleEventHandler.bind(this, key)
      : this.eventHandler.bind(this, key);

    document.addEventListener(eventName, handlerRef, false);

    this.mapper[key] = {
      eventName,
      handler: handlerRef,
      delay: throttleInterval,
      timerId: -1,
      reference: dotnetReference,
      elementId: document,
      properties,
      projection: null,
    };
  }

  /**
   * Handles throttled events by delaying the execution of the event handler.
   * @param key - The unique key identifying the subscription.
   * @param event - The event object.
   */
  private throttleEventHandler(key: string, event: Event): void {
    const entry = this.mapper[key];
    if (!entry) {
      return;
    }

    clearTimeout(entry.timerId);
    entry.timerId = window.setTimeout(
      this.eventHandler.bind(this, key, event),
      entry.delay
    );
  }

  /**
   * Handles the event by invoking the .NET method with the event data.
   * @param key - The unique key identifying the subscription.
   * @param event - The event object.
   */
  private eventHandler(key: string, event: Event): void {
    const entry = this.mapper[key];
    if (!entry) {
      return;
    }

    const elem = document.getElementById(entry.elementId as string);
    if (elem !== event.target && entry.elementId !== document) {
      return;
    }

    const eventEntry: { [key: string]: any } = {};
    for (const property of entry.properties) {
      eventEntry[property] = (event as any)[property];
    }

    if (entry.projection && typeof entry.projection === 'function') {
      entry.projection(eventEntry, event);
    }

    entry.reference.invokeMethodAsync('OnEventOccur', key, JSON.stringify(eventEntry));
  }

  /**
   * Unsubscribes from an event.
   * @param key - The unique key identifying the subscription.
   */
  unsubscribe(key: string): void {
    const entry = this.mapper[key];
    if (!entry) {
      return;
    }

    entry.reference = null;

    if (entry.elementId === document) {
      document.removeEventListener(entry.eventName, entry.handler, false);
    } else {
      const elem = document.getElementById(entry.elementId as string);
      if (elem) {
        elem.removeEventListener(entry.eventName, entry.handler, false);
      }
    }

    delete this.mapper[key];
  }
}